import { useState, useEffect } from 'react';
import { useTrainingStore, TrainingModule } from '../../../store/trainingStore';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Plus, Search, BookOpen, GraduationCap, Clock, Pencil, Trash2 } from 'lucide-react';
import { Input } from '../../../components/Input';
import { Textarea } from '../../../components/Textarea';
import { Switch } from '../../../components/Switch';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/Select';
import toast from 'react-hot-toast';

const TYPES = ['compliance', 'technical', 'soft-skills', 'onboarding', 'leadership', 'safety', 'other'];
const STATUSES = ['active', 'draft', 'archived'];

const EMPTY_FORM = {
    title: '', description: '', type: 'compliance',
    durationMinutes: '30', isMandatory: false, status: 'active',
};

export function TrainingCenter() {
    const { modules, isLoading, fetchModules, createModule, updateModule, deleteModule } = useTrainingStore();
    const [search, setSearch] = useState('');

    const [isFormOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchModules();
    }, []);

    const filteredModules = modules.filter(m =>
        m.title.toLowerCase().includes(search.toLowerCase()) ||
        m.type.toLowerCase().includes(search.toLowerCase())
    );

    const openCreate = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setErrors({});
        setFormOpen(true);
    };

    const openEdit = (m: TrainingModule) => {
        setEditingId(m.id);
        setForm({
            title: m.title ?? '',
            description: m.description ?? '',
            type: m.type ?? 'compliance',
            durationMinutes: String(m.durationMinutes ?? 30),
            isMandatory: !!m.isMandatory,
            status: m.status ?? 'active',
        });
        setErrors({});
        setFormOpen(true);
    };

    const validate = () => {
        const next: Record<string, string> = {};
        if (!form.title.trim()) next.title = 'Title is required';
        const dur = Number(form.durationMinutes);
        if (!form.durationMinutes || Number.isNaN(dur) || dur <= 0) next.durationMinutes = 'Enter a positive duration';
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const payload = {
                title: form.title.trim(),
                description: form.description.trim(),
                type: form.type,
                durationMinutes: Number(form.durationMinutes),
                isMandatory: form.isMandatory,
                status: form.status,
            };
            if (editingId) {
                await updateModule(editingId, payload);
                toast.success('Module updated');
            } else {
                await createModule(payload);
                toast.success('Module created');
            }
            setFormOpen(false);
            fetchModules();
        } catch (e) {
            toast.error(editingId ? 'Failed to update module' : 'Failed to create module');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteModule(deleteTarget.id);
            toast.success('Module deleted');
            setDeleteTarget(null);
        } catch (e) {
            toast.error('Failed to delete module');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Training Center</h1>
                    <p className="text-muted-foreground">Manage corporate learning modules and compliance courses.</p>
                </div>
                <Button variant="primary" onClick={openCreate}>
                    <Plus className="w-4 h-4 mr-2" />
                    New Module
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                            <BookOpen className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Active Modules</p>
                            <h3 className="text-foreground font-display text-metric">
                                {modules.filter(m => m.status === 'active').length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center text-warning">
                            <GraduationCap className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Mandatory Courses</p>
                            <h3 className="text-foreground font-display text-metric">
                                {modules.filter(m => m.isMandatory).length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center text-success">
                            <Clock className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Total Content Hours</p>
                            <h3 className="text-foreground font-display text-metric">
                                {Math.round(modules.reduce((acc, m) => acc + (m.durationMinutes || 0), 0) / 60)}h
                            </h3>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex justify-between items-center">
                        <h3 className="text-foreground font-display text-section-title">Course Catalog</h3>
                        <div className="relative w-64">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search courses..."
                                className="pl-9"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading...</div>
                    ) : (
                        <table className="w-full text-sm text-left">
                            <thead className="bg-secondary/50 text-muted-foreground font-semibold border-y border-border">
                                <tr>
                                    <th className="px-6 py-3">Course Title</th>
                                    <th className="px-6 py-3">Type</th>
                                    <th className="px-6 py-3">Duration</th>
                                    <th className="px-6 py-3">Mandatory</th>
                                    <th className="px-6 py-3">Status</th>
                                    <th className="px-6 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredModules.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                                            No training modules found.
                                        </td>
                                    </tr>
                                ) : filteredModules.map(m => (
                                    <tr key={m.id} className="hover:bg-secondary/20 transition-colors">
                                        <td className="px-6 py-4 font-medium text-foreground">
                                            {m.title}
                                            <div className="text-xs text-muted-foreground font-normal line-clamp-1">{m.description}</div>
                                        </td>
                                        <td className="px-6 py-4 capitalize">{m.type}</td>
                                        <td className="px-6 py-4">{m.durationMinutes} mins</td>
                                        <td className="px-6 py-4">
                                            {m.isMandatory ? (
                                                <Badge variant="destructive">Yes</Badge>
                                            ) : (
                                                <span className="text-muted-foreground">No</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge variant={m.status === 'active' ? 'success' : 'secondary'}>
                                                {m.status.toUpperCase()}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon-sm"
                                                    onClick={() => openEdit(m)}
                                                    className="text-muted-foreground hover:text-foreground"
                                                    aria-label={`Edit ${m.title}`}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon-sm"
                                                    onClick={() => setDeleteTarget({ id: m.id, title: m.title })}
                                                    className="text-muted-foreground hover:text-destructive"
                                                    aria-label={`Delete ${m.title}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>

            {/* Create / Edit modal */}
            <Dialog open={isFormOpen} onOpenChange={setFormOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingId ? 'Edit Module' : 'New Training Module'}</DialogTitle>
                        <DialogDescription>
                            {editingId ? 'Update the module details below.' : 'Create a learning or compliance module.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Title</label>
                            <Input
                                placeholder="e.g. Data Privacy & Security 2026"
                                value={form.title}
                                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                                invalid={!!errors.title}
                            />
                            {errors.title && <p className="text-xs text-destructive">{errors.title}</p>}
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Description</label>
                            <Textarea
                                rows={3}
                                placeholder="Short description of the module…"
                                value={form.description}
                                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Type</label>
                                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {TYPES.map((t) => (
                                            <SelectItem key={t} value={t} className="capitalize">{t.replace(/-/g, ' ')}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Duration (minutes)</label>
                                <Input
                                    type="number"
                                    min={1}
                                    value={form.durationMinutes}
                                    onChange={(e) => setForm((f) => ({ ...f, durationMinutes: e.target.value }))}
                                    invalid={!!errors.durationMinutes}
                                />
                                {errors.durationMinutes && <p className="text-xs text-destructive">{errors.durationMinutes}</p>}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 items-center">
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Status</label>
                                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {STATUSES.map((s) => (
                                            <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <label className="flex items-center gap-3 pt-6 cursor-pointer">
                                <Switch
                                    checked={form.isMandatory}
                                    onCheckedChange={(v) => setForm((f) => ({ ...f, isMandatory: v }))}
                                />
                                <span className="text-sm font-medium text-foreground">Mandatory for all employees</span>
                            </label>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setFormOpen(false)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleSubmit} isLoading={saving}>
                            {editingId ? 'Save Changes' : 'Create Module'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation */}
            <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <DialogContent className="max-w-[440px]">
                    <DialogHeader>
                        <DialogTitle>Delete module</DialogTitle>
                        <DialogDescription>
                            Delete <span className="font-medium text-foreground">{deleteTarget?.title}</span>? This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDelete} isLoading={deleting}>
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
